import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ImageIcon,
  Target,
  CheckCircle2,
  Circle,
  Calendar,
  FolderOpen,
  Eye
} from "lucide-react";
import { message, Spin } from "antd";
import axios from "../../utils/axios";

const ViewAnnotation = () => {
  const navigate = useNavigate();
  const location = useLocation()
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [images, setImages] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") ||
    "";
  const organizationId = localStorage.getItem("organizationId");
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  const fetchAnnotationProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await axios.post("/fetch-annotation-image", {
        username: user.username,
        projectname,
        organizationId
      });

      if (response.status === 200) {
      const allAnnotations = response.data.annotationImage || [];

      const formattedProjects = allAnnotations.map(item => ({
          projectName: item.projectname,
          annotationGroup: item.annotationGroup,
          version: item.version,
          images: item.images.map(img => ({
            imageUrl: img.imageUrl,
            width: img.width,
            height: img.height,
            splitType: img.splitType
          })),
          annotations: item.images.map(img => ({
          imageUrl: img.imageUrl,
          annotations: img.annotations
        })),
        createdOn: item.createdOn
      }));

      setProjects(formattedProjects);
      }
    } catch (error) {
        console.error(error);
        message.error("Failed to fetch annotation projects");
    } finally {
        setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnotationProjects();
    if (location.state?.selectedProject) {
      setSelectedProject(location.state.selectedProject);
      setImages(location.state.selectedProject.images || []);
      setAnnotations(location.state.selectedProject.annotations || []);
      setCurrentImageIndex(0);
    }
  }, [location.state]);

  const fetchProjectAnnotations = (project) => {
    setSelectedProject(project);
    setImages(project.images || []);
    setAnnotations(project.annotations || []);
    setCurrentImageIndex(0);
  };

  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !annotations.length) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);


    // 🔥 This is the missing piece
    
    const currentImageUrl = images[currentImageIndex]?.imageUrl || images[currentImageIndex];
    const currentImageAnnotations = annotations.find(
      img => img.imageUrl === currentImageUrl
    )?.annotations || [];

    currentImageAnnotations.forEach((annotation, index) => {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);

      const label = annotation.class || `Region ${index + 1}`;
      ctx.font = '12px Inter, system-ui, sans-serif';
      const textMetrics = ctx.measureText(label);
      const padding = 6;
      const labelWidth = textMetrics.width + padding * 2;
      const labelHeight = 20;

      if (annotation.type === "rectangle") {

  // 🔥 CASE 1: rectangle from DB (points)
  if (annotation.points?.length === 4) {
    ctx.beginPath();
    ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

    for (let i = 1; i < 4; i++) {
      ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
    }

    ctx.closePath();
    ctx.stroke();

    // ✅ LABEL POSITION
    const firstPoint = annotation.points[0];
    ctx.fillStyle = '#3b82f6';
    ctx.fillRect(firstPoint.x, firstPoint.y - labelHeight, labelWidth, labelHeight);
    ctx.fillStyle = 'white';
    ctx.fillText(label, firstPoint.x + padding, firstPoint.y - labelHeight / 2);
  }

  // 🟢 fallback (old format)
  else {
    ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);
  }
}
      else if (annotation.type === "circle") {
        ctx.beginPath();
        ctx.arc(annotation.x, annotation.y, annotation.radius, 0, 2 * Math.PI);
        ctx.stroke();

        ctx.fillStyle = '#3b82f6';
        ctx.fillRect(annotation.x - labelWidth / 2, annotation.y - annotation.radius - labelHeight, labelWidth, labelHeight);
        ctx.fillStyle = 'white';
        ctx.fillText(label, annotation.x - textMetrics.width / 2, annotation.y - annotation.radius - labelHeight / 2);
      }
      else if (annotation.type === "polygon" || annotation.type === "polyline") {
        const points = annotation.points || [];
        if (points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          if (annotation.type === "polygon" || annotation.type === "polyline") {
            ctx.closePath();
          }
          ctx.stroke();

          const firstPoint = points[0];
          ctx.fillStyle = '#3b82f6';
          ctx.fillRect(firstPoint.x, firstPoint.y - labelHeight, labelWidth, labelHeight);
          ctx.fillStyle = 'white';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, firstPoint.x + padding, firstPoint.y - labelHeight / 2);
        }
      }
    });
  }, [annotations, images, currentImageIndex]);

  const drawSpotlightOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !annotations.length) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentImageUrl = images[currentImageIndex]?.imageUrl || images[currentImageIndex];
    const currentImageAnnotations = annotations.find(
      img => img.imageUrl === currentImageUrl
    )?.annotations || [];

    if (currentImageAnnotations.length === 0) return;

    // Fill entire canvas with semi-transparent black
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Create path with annotation areas to clear (spotlight effect)
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';

    currentImageAnnotations.forEach((annotation) => {
      if (annotation.type === "rectangle") {

        if (annotation.points?.length === 4) {
          ctx.beginPath();
          ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

          for (let i = 1; i < 4; i++) {
            ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
          }

          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillRect(annotation.x, annotation.y, annotation.width, annotation.height);
        }
      }
      else if (annotation.type === "circle") {
        ctx.beginPath();
        ctx.arc(annotation.x, annotation.y, annotation.radius, 0, 2 * Math.PI);
        ctx.fill();
      }
      else if (annotation.type === "polygon" || annotation.type === "polyline") {
        const points = annotation.points || [];
        if (points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          if (annotation.type === "polygon" || annotation.type === "polyline") {
            ctx.closePath();
          }
          ctx.fill();
        }
      }
    });

    ctx.globalCompositeOperation = 'source-over';
  }, [annotations, images, currentImageIndex]);

  const handleImageLoad = useCallback(() => {
  const img = imageRef.current;
  const canvas = canvasRef.current;
  const overlay = overlayCanvasRef.current;

  if (!img || !canvas || !overlay) return;

  const rect = img.getBoundingClientRect();

  // Drawing resolution = original pixels
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;

  overlay.width = img.naturalWidth;
  overlay.height = img.naturalHeight;

  // Display size = visible size
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;

  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;

  drawAnnotations();
  drawSpotlightOverlay();
}, [drawAnnotations, drawSpotlightOverlay]);

  useEffect(() => {
    if (selectedProject && images.length > 0) {
      drawAnnotations();
      drawSpotlightOverlay();
    }
  }, [drawAnnotations, drawSpotlightOverlay, selectedProject]);

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : images.length - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => 
      prev < images.length - 1 ? prev + 1 : 0
    );
  };

  const getCurrentImageAnnotations = () => {
    if (!images.length || !annotations.length) return [];
    const currentImageUrl = images[currentImageIndex]?.imageUrl || images[currentImageIndex];
    return annotations.find(img => img.imageUrl === currentImageUrl)?.annotations || [];
  };

  const handleCreateVersion = () => {
    // Ensure selectedProject includes updated images with width and height
    const updatedProject = {
      ...selectedProject,
      images: images // Use the updated images state that includes width and height
    };
    navigate("/sidebar/Datasetversion", { state: { selectedProject: updatedProject } });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Projects List View
  if (!selectedProject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {projectsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spin size="large" tip="Loading projects..." />
            </div>
          ) : projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-center p-8">
                <FolderOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-600 mb-2">
                  No Annotations Found
                </h2>
                <p className="text-slate-500 mb-4">
                  You haven't created any annotation for this project
                </p>
                {/* <button
                  onClick={() => navigate("/sidebar/UploadData")}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create New Project
                </button> */}
              </div>
            </div>
          ) : (
            <div>

              <div className="mb-8">
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  View Annotations
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Browse your saved annotation projects</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, index) => (
                  <div
                    key={index}
                    onClick={() => fetchProjectAnnotations(project)}
                    className="group bg-white rounded-2xl shadow-lg border border-slate-200/60 hover:shadow-xl hover:border-slate-300/60 transition-all duration-300 cursor-pointer overflow-hidden"
                  >
                    <div className="p-6">
                      {/* Project Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                              {project.projectName}
                            </h3>
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>

                      {/* Project Details */}
                      <div className="space-y-3">
                        {/* <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50 to-slate-50/50 rounded-lg"> */}
                          {/* <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <Users className="w-4 h-4" />
                            <span>Annotation Group</span>
                          </div> */}
                          {/* <span className="text-sm font-medium text-slate-700">
                            {project.annotationGroup}
                          </span> */}
                        {/* </div> */}

                        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-emerald-50/50 rounded-lg">
                          <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <ImageIcon className="w-4 h-4" />
                            <span>Images</span>
                          </div>
                          <span className="text-sm font-medium text-emerald-600">
                            {project.images.length}
                          </span>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-50/50 rounded-lg">
                          <div className="flex items-center space-x-2 text-sm text-slate-600">
                            <Target className="w-4 h-4" />
                            <span>Annotations</span>
                          </div>
                          <span className="text-sm font-medium text-purple-600">
                            {project.annotations.reduce((total, img) => total + (img.annotations ? img.annotations.length : 0),0)}
                          </span>
                        </div>

                        {project.createdOn && (
                          <div className="flex items-center space-x-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
                            <Calendar className="w-3 h-3" />
                            <span>Created on {formatDate(project.createdOn)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hover overlay */}
                    <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Image Viewer with Annotations
  const currentImageAnnotations = getCurrentImageAnnotations();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSelectedProject(null)}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-0.5 transition-transform" />
                <span className="font-medium">Back to Projects</span>
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  {selectedProject.projectName}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Group: {selectedProject.annotationGroup} • Viewing saved annotations
                </p>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-sm">
                <div className="flex items-center space-x-1 text-slate-600">
                  <Circle className="w-3 h-3" />
                  <span>{currentImageIndex + 1} of {images.length}</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1 text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{currentImageAnnotations.length} annotations</span>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-300"
                  style={{ width: `${((currentImageIndex + 1) / images.length) * 100}%` }}
                ></div>
              </div>

              <button
                onClick={handleCreateVersion}
                className="flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <span className="font-medium">Create Version</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-50">
          <Spin size="large" tip="Loading annotations..." />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main viewing area */}
          <div className="xl:col-span-3 space-y-6">
            {/* Image viewer */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60">
              {/* Image navigation */}
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200/60">
                <button
                  onClick={goToPrevious}
                  disabled={images.length <= 1}
                  className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <span className="font-medium">Previous</span>
                </button>
                
                <div className="text-center">
                  <div className="text-sm font-semibold text-slate-700">
                    Image {currentImageIndex + 1} of {images.length}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {currentImageAnnotations.length} annotation{currentImageAnnotations.length !== 1 ? 's' : ''}
                  </div>
                </div>
                
                <button
                  onClick={goToNext}
                  disabled={images.length <= 1}
                  className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                >
                  <span className="font-medium">Next</span>
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Annotation Viewer */}
              <div className="p-8 bg-gradient-to-br from-slate-50/50 to-white">
                {images.length > 0 ? (
                  <div className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200/60 max-w-full">
                    <img
  ref={imageRef}
  src={images[currentImageIndex]?.imageUrl || images[currentImageIndex]}
  alt={`Annotated image ${currentImageIndex + 1}`}
  className="max-w-full h-auto block"
  onLoad={() => handleImageLoad()}
/>

                    <canvas
                      ref={canvasRef}
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                    />
                    <canvas
                      ref={overlayCanvasRef}
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                    />
                    {images[currentImageIndex]?.splitType && (
                      <div className="absolute top-3 left-3 flex items-center space-x-2 bg-purple-50 border-2 border-purple-500 text-purple-600 text-sm font-semibold px-3 py-1.5 rounded-xl shadow-sm">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 text-purple-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="capitalize">{images[currentImageIndex].splitType}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No images found in this project</p>
                  </div>
                )}
                
                {/* No annotations message */}
                {images.length > 0 && currentImageAnnotations.length === 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                      No annotations found for this image
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Image thumbnails gallery */}
            {images.length > 1 && (
              <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 border border-slate-200/60">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                    <ImageIcon className="w-5 h-5 mr-2 text-slate-600" />
                    Image Gallery
                  </h3>
                  <div className="text-sm text-slate-500">
                    {images.length} images
                  </div>
                </div>

                <div className="flex space-x-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                  {images.map((image, index) => {
                    // ✅ Access imageUrl safely
                    const imageUrl = image.imageUrl || image;
                    const imageAnnotations =
                      annotations.find(img => img.imageUrl === imageUrl)?.annotations || [];

                    return (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 relative group transition-all duration-200 ${
                          index === currentImageIndex
                            ? "ring-2 ring-blue-500 ring-offset-2 scale-105"
                            : "ring-1 ring-slate-200 hover:ring-slate-300 hover:scale-102"
                        } rounded-xl overflow-hidden`}
                      >
                        <img
                          src={imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-20 h-20 object-cover transition-all duration-200 group-hover:brightness-110"
                        />

                        {/* ✅ Show splitType on thumbnail */}
                        {image.splitType && (
                          <div className="absolute top-1 left-1 flex items-center space-x-1 bg-purple-50 border border-purple-400 text-purple-600 text-[10px] font-semibold px-2 py-0.5 rounded-lg shadow-sm">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3 text-purple-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="capitalize">{image.splitType}</span>
                          </div>
                        )}

                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-1 left-1 text-white text-xs font-medium">
                            {index + 1}
                          </div>
                        </div>

                        {/* Annotation count badge */}
                        {imageAnnotations.length > 0 && (
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white shadow-md">
                            {imageAnnotations.length}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 sticky top-24">
              {/* Current image annotations */}
              <div className="p-6 border-b border-slate-200/60">
                <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  Current Annotations
                </h2>
                
                {currentImageAnnotations.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Target className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">No annotations</p>
                    <p className="text-slate-400 text-xs mt-1">This image has no saved annotations</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                    {currentImageAnnotations.map((annotation, idx) => (
                      <div 
                        key={idx}
                        className="group p-4 rounded-xl border bg-gradient-to-br from-slate-50 to-white border-slate-200/60 hover:border-slate-300/60 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-sm text-slate-700 flex items-center">
                            <div className="w-3 h-3 rounded-full mr-2 bg-blue-500"></div>
                            {annotation.class || `Region ${idx + 1}`}
                          </div>
                          <div className="text-xs text-slate-400">#{idx + 1}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">X:</span> {Math.round(annotation.x)}
                          </div>
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">Y:</span> {Math.round(annotation.y)}
                          </div>
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">W:</span> {Math.round(annotation.width)}
                          </div>
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">H:</span> {Math.round(annotation.height)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Project summary */}
              <div className="p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Project Statistics
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-50/50 rounded-lg border border-blue-100">
                    <span className="text-sm font-medium text-slate-600">Total Images</span>
                    <span className="text-sm font-bold text-blue-600">{images.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-emerald-50/50 rounded-lg border border-emerald-100">
                    <span className="text-sm font-medium text-slate-600">Annotated Images</span>
                    <span className="text-sm font-bold text-emerald-600">
                      {annotations.filter(img => img.annotations && img.annotations.length > 0).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-50/50 rounded-lg border border-purple-100">
                    <span className="text-sm font-medium text-slate-600">Total Annotations</span>
                    <span className="text-sm font-bold text-purple-600">
                      {annotations.reduce((total, img) => total + (img.annotations ? img.annotations.length : 0), 0)}
                    </span>
                  </div>
                </div>
                
                {/* Project info */}
                <div className="mt-4 p-4 bg-gradient-to-r from-slate-50 to-slate-50/50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Project:</span>
                      <span className="font-medium">{selectedProject.projectName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className="font-medium">{selectedProject.projectType || 'Detection'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Group:</span>
                      <span className="font-medium">{selectedProject.annotationGroup}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Version:</span>
                      <span className="font-medium">{selectedProject.version}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAnnotation;